import { TipoEvaluacion } from 'types/models';

export type FieldType = 'text' | 'number' | 'select' | 'multiselect' | 'boolean' | 'score';

export interface TemplateField {
  id: string;
  label: string;
  type: FieldType;
  options?: string[];
  min?: number;
  max?: number;
  unit?: string;
  placeholder?: string;
  dependsOn?: { field: string; value: unknown };
}

export interface TemplateSection {
  id: string;
  label: string;
  fields: TemplateField[];
}

export interface EvaluationTest {
  id: string;
  nombre: string;
  tipo: 'score' | 'boolean' | 'select' | 'text';
  unit?: string;
  options?: string[];
  interpretacion?: Record<string, string>;
}

export interface EvaluationTypeConfig {
  tipo: TipoEvaluacion;
  label: string;
  color: string;
  description: string;
  sections: TemplateSection[];
  tests: EvaluationTest[];
}

// ─── Geriátrica ───────────────────────────────────────────────────────────────

const geriatricaConfig: EvaluationTypeConfig = {
  tipo: TipoEvaluacion.GERIATRICA,
  label: 'Geriátrica',
  color: 'purple',
  description: 'Evaluación funcional del adulto mayor',
  sections: [
    {
      id: 'funcional',
      label: 'Funcionalidad',
      fields: [
        { id: 'barthel', label: 'Índice de Barthel (ADL)', type: 'score', min: 0, max: 100, unit: 'pts' },
        { id: 'marcha', label: 'Marcha', type: 'select', options: ['Independiente', 'Asistida', 'Dependiente', 'Imposible'] },
        { id: 'equilibrio', label: 'Equilibrio', type: 'select', options: ['Normal', 'Alterado leve', 'Alterado severo'] },
        { id: 'ayudasTecnicas', label: 'Ayudas técnicas', type: 'text', placeholder: 'Bastón, andador, silla de ruedas...' },
      ],
    },
    {
      id: 'cognitivo',
      label: 'Estado Cognitivo',
      fields: [
        { id: 'mmse', label: 'MMSE (Folstein)', type: 'score', min: 0, max: 30, unit: 'pts/30' },
        { id: 'orientacion', label: 'Orientación T-E', type: 'select', options: ['Conservada', 'Parcialmente conservada', 'Alterada'] },
      ],
    },
    {
      id: 'riesgos',
      label: 'Riesgos',
      fields: [
        { id: 'tinetti', label: 'Escala de Tinetti (Caídas)', type: 'score', min: 0, max: 28, unit: 'pts/28' },
        { id: 'caidas12m', label: 'Caídas en últimos 12 meses', type: 'number', min: 0 },
        { id: 'miedo_caida', label: 'Miedo a caer', type: 'select', options: ['Ninguno', 'Leve', 'Moderado', 'Severo'] },
      ],
    },
  ],
  tests: [
    { id: 'tugTest', nombre: 'Timed Up & Go (TUG)', tipo: 'score', unit: 'seg', interpretacion: { '<10': 'Bajo riesgo', '10-20': 'Riesgo moderado', '>20': 'Alto riesgo' } },
    { id: 'berg', nombre: 'Escala de Equilibrio de Berg', tipo: 'score', unit: 'pts/56', interpretacion: { '41-56': 'Bajo riesgo', '21-40': 'Riesgo moderado', '0-20': 'Alto riesgo' } },
    { id: 'mmseTest', nombre: 'MMSE (Estado Cognitivo)', tipo: 'score', unit: 'pts/30', interpretacion: { '27-30': 'Normal', '21-26': 'Deterioro leve', '10-20': 'Deterioro moderado', '<10': 'Deterioro severo' } },
    { id: 'sppb', nombre: 'Short Physical Performance Battery (SPPB)', tipo: 'score', unit: 'pts/12' },
    { id: 'yesavage', nombre: 'Escala de Depresión de Yesavage', tipo: 'score', unit: 'pts/15' },
  ],
};

// ─── Traumatológica ───────────────────────────────────────────────────────────

const traumatologicaConfig: EvaluationTypeConfig = {
  tipo: TipoEvaluacion.TRAUMATOLOGICA,
  label: 'Traumatológica',
  color: 'orange',
  description: 'Evaluación músculo-esquelética y ortopédica',
  sections: [
    {
      id: 'lesion',
      label: 'Lesión',
      fields: [
        { id: 'regionAfectada', label: 'Región anatómica', type: 'multiselect', options: ['Cervical', 'Dorsal', 'Lumbar', 'Hombro D', 'Hombro I', 'Codo D', 'Codo I', 'Muñeca D', 'Muñeca I', 'Mano D', 'Mano I', 'Cadera D', 'Cadera I', 'Rodilla D', 'Rodilla I', 'Tobillo D', 'Tobillo I', 'Pie D', 'Pie I'] },
        { id: 'mecanismoLesion', label: 'Mecanismo de lesión', type: 'select', options: ['Traumatismo directo', 'Traumatismo indirecto', 'Sobreuso/crónico', 'Espontáneo', 'Postquirúrgico', 'Postinmovilización', 'Otro'] },
        { id: 'tiempoEvolucion', label: 'Tiempo de evolución', type: 'text', placeholder: 'Ej: 3 semanas, 2 meses...' },
      ],
    },
    {
      id: 'exploracion',
      label: 'Exploración',
      fields: [
        { id: 'edema', label: 'Edema', type: 'select', options: ['Ausente', '+/4', '++/4', '+++/4'] },
        { id: 'romFlexion', label: 'ROM Flexión (°)', type: 'number', min: 0, max: 180, unit: '°' },
        { id: 'romExtension', label: 'ROM Extensión (°)', type: 'number', min: 0, max: 90, unit: '°' },
        { id: 'romAbduccion', label: 'ROM Abducción (°)', type: 'number', min: 0, max: 180, unit: '°' },
        { id: 'romRotacion', label: 'ROM Rotación (°)', type: 'number', min: 0, max: 90, unit: '°' },
        { id: 'fuerzaDaniels', label: 'Fuerza Muscular (Daniels 0-5)', type: 'score', min: 0, max: 5 },
      ],
    },
    {
      id: 'funcional',
      label: 'Funcional',
      fields: [
        { id: 'limitacionFuncional', label: 'Limitación funcional', type: 'select', options: ['Ninguna', 'Leve', 'Moderada', 'Severa', 'Total'] },
        { id: 'actividadLaboral', label: 'Actividad laboral afectada', type: 'boolean' },
        { id: 'avd', label: 'AVD afectadas', type: 'text', placeholder: 'Actividades de vida diaria comprometidas...' },
      ],
    },
  ],
  tests: [
    { id: 'lasegue', nombre: 'Test de Lasègue', tipo: 'select', options: ['Negativo', 'Positivo D', 'Positivo I', 'Bilateral'] },
    { id: 'lachman', nombre: 'Prueba de Lachman (LCA)', tipo: 'select', options: ['Negativo', 'Grado I', 'Grado II', 'Grado III'] },
    { id: 'mcmurray', nombre: 'Prueba de McMurray (Menisco)', tipo: 'select', options: ['Negativo', 'Positivo medial', 'Positivo lateral'] },
    { id: 'spurling', nombre: 'Test de Spurling (Cervical)', tipo: 'select', options: ['Negativo', 'Positivo D', 'Positivo I'] },
    { id: 'valgusStress', nombre: 'Test de Estrés en Valgo (LLI)', tipo: 'select', options: ['Negativo', 'Grado I', 'Grado II', 'Grado III'] },
    { id: 'oswestry', nombre: 'Índice de Discapacidad de Oswestry', tipo: 'score', unit: '%', interpretacion: { '0-20': 'Discapacidad mínima', '21-40': 'Discapacidad moderada', '41-60': 'Discapacidad severa', '61-80': 'Discapacidad muy severa', '81-100': 'Discapacidad máxima' } },
    { id: 'ndi', nombre: 'Neck Disability Index (NDI)', tipo: 'score', unit: 'pts/50' },
    { id: 'dash', nombre: 'DASH (Miembro Superior)', tipo: 'score', unit: 'pts/100' },
    { id: 'koos', nombre: 'KOOS (Rodilla)', tipo: 'score', unit: 'pts/100' },
  ],
};

// ─── Deportiva ────────────────────────────────────────────────────────────────

const deportivaConfig: EvaluationTypeConfig = {
  tipo: TipoEvaluacion.DEPORTIVA,
  label: 'Deportiva',
  color: 'green',
  description: 'Evaluación del deportista y retorno a la actividad',
  sections: [
    {
      id: 'perfil',
      label: 'Perfil Deportivo',
      fields: [
        { id: 'deporte', label: 'Deporte / Actividad', type: 'text', placeholder: 'Fútbol, natación, crossfit...' },
        { id: 'nivel', label: 'Nivel competitivo', type: 'select', options: ['Recreacional', 'Amateur', 'Semiprofesional', 'Profesional'] },
        { id: 'posicion', label: 'Posición / Especialidad', type: 'text', placeholder: 'Delantero, portero, velocista...' },
        { id: 'horasSemanales', label: 'Horas de entrenamiento/semana', type: 'number', min: 0, unit: 'h' },
      ],
    },
    {
      id: 'lesion',
      label: 'Historia de Lesión',
      fields: [
        { id: 'lesionPrevia', label: 'Lesión previa en misma zona', type: 'boolean' },
        { id: 'lesionPreviaDesc', label: 'Descripción lesión previa', type: 'text', placeholder: 'Describe lesión anterior si aplica...' },
        { id: 'diasParado', label: 'Días sin entrenar', type: 'number', min: 0, unit: 'días' },
      ],
    },
    {
      id: 'retorno',
      label: 'Retorno Deportivo',
      fields: [
        { id: 'estadoRetorno', label: 'Estado de retorno deportivo', type: 'select', options: ['No apto', 'Apto para entrenamiento ligero', 'Apto para entrenamiento completo', 'Apto para competencia'] },
        { id: 'criteriosRetorno', label: 'Criterios de retorno cumplidos', type: 'text', placeholder: 'Criterios funcionales, fuerza, dolor...' },
      ],
    },
  ],
  tests: [
    { id: 'singleLegHop', nombre: 'Single Leg Hop Test', tipo: 'score', unit: 'cm' },
    { id: 'tripleHop', nombre: 'Triple Hop Test', tipo: 'score', unit: 'cm' },
    { id: 'crossoverHop', nombre: 'Crossover Hop Test', tipo: 'score', unit: 'cm' },
    { id: 'yBalance', nombre: 'Y-Balance Test', tipo: 'score', unit: 'cm' },
    { id: 'ikdc', nombre: 'IKDC (Rodilla)', tipo: 'score', unit: 'pts/100' },
    { id: 'faos', nombre: 'FAOS (Tobillo)', tipo: 'score', unit: 'pts/100' },
    { id: 'limb_symmetry', nombre: 'Limb Symmetry Index (LSI)', tipo: 'score', unit: '%', interpretacion: { '>=90': 'Apto retorno', '<90': 'No apto' } },
    { id: 'rsi', nombre: 'Psychological Readiness (ACL-RSI)', tipo: 'score', unit: 'pts/100' },
  ],
};

// ─── Pediátrica ───────────────────────────────────────────────────────────────

const pediatricaConfig: EvaluationTypeConfig = {
  tipo: TipoEvaluacion.PEDIATRICA,
  label: 'Pediátrica',
  color: 'teal',
  description: 'Evaluación del neurodesarrollo y motricidad infantil',
  sections: [
    {
      id: 'general',
      label: 'Datos Generales',
      fields: [
        { id: 'edadCronologica', label: 'Edad cronológica', type: 'text', placeholder: 'Ej: 18 meses, 3 años 2 meses' },
        { id: 'edadCorregida', label: 'Edad corregida (si aplica)', type: 'text', placeholder: 'Solo para prematuros' },
        { id: 'diagnosticoPrevio', label: 'Diagnóstico previo', type: 'text', placeholder: 'PCI, SD, TEA...' },
      ],
    },
    {
      id: 'tono',
      label: 'Tono y Postura',
      fields: [
        { id: 'tonoMuscular', label: 'Tono muscular', type: 'select', options: ['Hipertónico', 'Normal', 'Hipotónico'] },
        { id: 'patron_postural', label: 'Patrón postural', type: 'select', options: ['Normal', 'Asimétrico', 'Alterado'] },
        { id: 'control_cabeza', label: 'Control cefálico', type: 'select', options: ['Presente', 'Parcial', 'Ausente', 'No aplica por edad'] },
      ],
    },
    {
      id: 'hitos',
      label: 'Hitos Motores',
      fields: [
        { id: 'volteo', label: 'Volteo', type: 'select', options: ['Presente', 'En proceso', 'Ausente', 'No aplica'] },
        { id: 'sedestacion', label: 'Sedestación independiente', type: 'select', options: ['Presente', 'Con apoyo', 'Ausente', 'No aplica'] },
        { id: 'gateo', label: 'Gateo / desplazamiento', type: 'select', options: ['Presente', 'En proceso', 'Ausente', 'No aplica'] },
        { id: 'bipedestacion', label: 'Bipedestación', type: 'select', options: ['Independiente', 'Con apoyo', 'Ausente', 'No aplica'] },
        { id: 'marcha', label: 'Marcha', type: 'select', options: ['Independiente', 'Asistida', 'Ausente', 'No aplica'] },
      ],
    },
    {
      id: 'reflejos',
      label: 'Reflejos',
      fields: [
        { id: 'reflejoMoro', label: 'Reflejo de Moro', type: 'select', options: ['Presente', 'Ausente', 'Integrado', 'No aplica'] },
        { id: 'reflejo_babinski', label: 'Reflejo de Babinski', type: 'select', options: ['Presente (normal < 2a)', 'Presente (patológico)', 'Ausente', 'No aplica'] },
        { id: 'reflejo_palmar', label: 'Reflejo de prensión palmar', type: 'select', options: ['Presente', 'Ausente', 'Integrado', 'No aplica'] },
        { id: 'reacciones_equilibrio', label: 'Reacciones de equilibrio', type: 'select', options: ['Presentes', 'En desarrollo', 'Ausentes'] },
      ],
    },
  ],
  tests: [
    { id: 'denverII', nombre: 'Denver II (Desarrollo Psicomotor)', tipo: 'select', options: ['Normal', 'Sospechoso', 'Anormal', 'No confiable'] },
    { id: 'peabody', nombre: 'Peabody Developmental Motor Scales (PDMS-2)', tipo: 'score', unit: 'GMQ/FMQ' },
    { id: 'bayley', nombre: 'Bayley Scales of Infant Development', tipo: 'score', unit: 'compuesto' },
    { id: 'gmfcs', nombre: 'GMFCS (PCI - Sistema de Clasificación Función Motora)', tipo: 'select', options: ['Nivel I', 'Nivel II', 'Nivel III', 'Nivel IV', 'Nivel V'] },
    { id: 'aims', nombre: 'Alberta Infant Motor Scale (AIMS)', tipo: 'score', unit: 'percentil' },
    { id: 'weefim', nombre: 'WeeFIM (Función Independencia)', tipo: 'score', unit: 'pts/126' },
  ],
};

// ─── Export ───────────────────────────────────────────────────────────────────

export const EVALUATION_TYPE_CONFIGS: Record<TipoEvaluacion, EvaluationTypeConfig> = {
  [TipoEvaluacion.GERIATRICA]:     geriatricaConfig,
  [TipoEvaluacion.TRAUMATOLOGICA]: traumatologicaConfig,
  [TipoEvaluacion.DEPORTIVA]:      deportivaConfig,
  [TipoEvaluacion.PEDIATRICA]:     pediatricaConfig,
};

export const TIPO_EVALUACION_META: Record<TipoEvaluacion, { label: string; color: string; description: string }> = {
  [TipoEvaluacion.GERIATRICA]:     { label: 'Geriátrica',     color: 'purple', description: 'Adulto mayor — funcionalidad, caídas, estado cognitivo' },
  [TipoEvaluacion.TRAUMATOLOGICA]: { label: 'Traumatológica', color: 'orange', description: 'Músculo-esquelético — ROM, fuerza, pruebas ortopédicas' },
  [TipoEvaluacion.DEPORTIVA]:      { label: 'Deportiva',      color: 'green',  description: 'Deportista — perfil, lesión, retorno a la actividad' },
  [TipoEvaluacion.PEDIATRICA]:     { label: 'Pediátrica',     color: 'teal',   description: 'Infantil — tono, hitos motores, neurodesarrollo' },
};
